import './index.css'
import { useState, useEffect, useRef } from 'react'
import { X, ArrowDownRight, ArrowUpRight, CreditCard, Receipt, Plus, Check, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react'
import type { AddTransactionSheetProps, TransactionKind, AIParseResult, AIBatchParseResult } from '../../types'
import { maskCurrency, digitsToAmount } from '../../lib/format'
import { supabase } from '../../lib/supabase'
import AIImportButton, { type AIImportState } from '../AIImportButton'
import AIImportModal, { type AIMediaType } from '../AIImportModal'

type CardForm = { name: string; last4: string; tone: string; limitDigits: string; due: string; close: string }
const PRESET_COLORS: string[] = [
  '#1F3D2E', '#242321', '#B43F30', '#B47812', '#FF6B00', '#CC1429',
  '#6A05A8', '#003D87', '#0070BB', '#1FA55E', '#006937', '#00C1B4',
]
const BLANK_CARD: CardForm = { name: '', last4: '', tone: '#1F3D2E', limitDigits: '', due: '', close: '' }

function validateCardForm(f: CardForm): string | null {
  if (!f.name.trim()) return 'Nome obrigatório'
  if (f.last4.trim() && !/^\d{4}$/.test(f.last4.trim())) return 'Informe exatamente 4 dígitos'
  if (!f.limitDigits) return 'Limite obrigatório'
  const due = parseInt(f.due.trim(), 10)
  if (isNaN(due) || due < 1 || due > 31) return 'Dia de vencimento inválido (1–31)'
  const close = parseInt(f.close.trim(), 10)
  if (isNaN(close) || close < 1 || close > 31) return 'Dia de fechamento inválido (1–31)'
  return null
}

function validateDigits(digits: string): boolean {
  return digits.length > 0 && Number(digits) > 0
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0]
}

export default function AddTransactionSheet({ open, onClose, onSubmit, onBatchSubmit, onUpdate, categories, subcategories, cards, addCard, addCategory, editing, planTier = 'free' }: AddTransactionSheetProps) {
  const [kind,            setKind]            = useState<TransactionKind | null>(null)
  const [expenseType,     setExpenseType]     = useState<'fixed' | 'card' | null>(null)
  const [cardId,          setCardId]          = useState('')
  const [title,           setTitle]           = useState('')
  const [amountDigits,    setAmountDigits]    = useState('')
  const [categoryId,      setCategoryId]      = useState('')
  const [subcategoryId,   setSubcategoryId]   = useState('')
  const [recurring,       setRecurring]       = useState(false)
  const [date,            setDate]            = useState(todayIso())
  const [dueDate,         setDueDate]         = useState('')
  const [recurringUntil,  setRecurringUntil]  = useState('')
  const [errors,          setErrors]          = useState<{ kind?: string; expenseType?: string; title?: string; amount?: string; date?: string; card?: string }>({})
  const [submitting,      setSubmitting]      = useState(false)
  const [submitError,     setSubmitError]     = useState<string | null>(null)
  const [showCardForm,      setShowCardForm]      = useState(false)
  const [cardForm,          setCardForm]          = useState<CardForm>(BLANK_CARD)
  const [cardFormError,     setCardFormError]     = useState<string | null>(null)
  const [cardFormSaving,    setCardFormSaving]    = useState(false)
  const [showCategoryForm,       setShowCategoryForm]       = useState(false)
  const [categoryFormName,       setCategoryFormName]       = useState('')
  const [categoryFormLimitDigits,setCategoryFormLimitDigits]= useState('')
  const [categoryFormError,      setCategoryFormError]      = useState<string | null>(null)
  const [categoryFormSaving,     setCategoryFormSaving]     = useState(false)
  const [realized,               setRealized]               = useState(false)
  const [installmentCount,       setInstallmentCount]       = useState(1)
  const [installmentMode,        setInstallmentMode]        = useState<'per_installment' | 'total'>('per_installment')
  const [aiState,                setAiState]                = useState<AIImportState>('idle')
  const [aiModalOpen,            setAiModalOpen]            = useState(false)
  const [aiClarificationFields,  setAiClarificationFields]  = useState<string[]>([])
  const [toast,                  setToast]                  = useState<string | null>(null)
  const [showTips,               setShowTips]               = useState(false)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (open) {
      if (editing) {
        setKind(editing.kind)
        setExpenseType(editing.card_id ? 'card' : 'fixed')
        setCardId(editing.card_id ?? '')
        setTitle(editing.installment_group_id
          ? editing.title.replace(/\s*\(\d+\/\d+\)$/, '').trim()
          : editing.title)
        setAmountDigits(Math.round(editing.amount * 100).toString())
        setCategoryId(editing.category_id ?? '')
        setSubcategoryId(editing.subcategory_id ?? '')
        setRecurring(editing.recurring)
        setRealized(editing.realized)
        setDate(editing.date_iso)
        setDueDate(editing.due_date ?? '')
        setRecurringUntil(editing.recurring_until ?? '')
        setInstallmentCount(editing.installment_count ?? 1)
        setInstallmentMode('per_installment')
      } else {
        setKind(null)
        setExpenseType(null)
        setCardId('')
        setTitle('')
        setAmountDigits('')
        setCategoryId('')
        setSubcategoryId('')
        setRecurring(false)
        setRealized(false)
        setDate(todayIso())
        setDueDate('')
        setRecurringUntil('')
        setInstallmentCount(1)
        setInstallmentMode('per_installment')
      }
      setErrors({})
      setSubmitting(false)
      setSubmitError(null)
      setShowCardForm(false)
      setCardForm(BLANK_CARD)
      setCardFormError(null)
      setShowCategoryForm(false)
      setCategoryFormName('')
      setCategoryFormLimitDigits('')
      setCategoryFormError(null)
      setAiState('idle')
      setAiModalOpen(false)
      setAiClarificationFields([])
      setToast(null)
      setShowTips(false)
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const cats    = kind ? categories.filter(c => c.kind === kind) : []
  const subCats = subcategories.filter(s => s.parent_id === categoryId)
  const activeCards = cards.filter(c => !c.is_archived || (editing && editing.card_id === c.id))
  const isCardInstallment = kind === 'out' && expenseType === 'card' && installmentCount > 1
  const canShowMainFields = kind === 'in' || (kind === 'out' && expenseType !== null)
  const canRepeat = canShowMainFields && !isCardInstallment

  function switchKind(next: TransactionKind) {
    setKind(next)
    setCategoryId('')
    setSubcategoryId('')
    if (errors.kind) setErrors(prev => ({ ...prev, kind: undefined }))
    if (next === 'in') { setExpenseType(null); setCardId('') }
  }

  function selectCategory(id: string) {
    setCategoryId(id)
    setSubcategoryId('')
  }

  function showToast(msg: string) {
    setToast(msg)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 4000)
  }

  function resolveCategoryId(suggestion: string, kind: TransactionKind | null): string {
    if (!suggestion) return ''
    const allCats = categories.filter(c => kind ? c.kind === kind : true)
    const match = allCats.find(c =>
      c.name.toLowerCase().includes(suggestion.toLowerCase()) ||
      suggestion.toLowerCase().includes(c.name.toLowerCase())
    )
    return match?.id ?? ''
  }

  async function handleAiFile(file: File, mediaType: AIMediaType) {
    setAiState('loading')
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('mediaType', mediaType)
      const { data, error } = await supabase.functions.invoke<AIParseResult | AIBatchParseResult>('parse-transaction-media', { body: form })
      if (error || !data) throw new Error('could_not_parse')

      // Batch: multiple transactions from a statement or spreadsheet
      if ('transactions' in data && Array.isArray(data.transactions)) {
        const txs = data.transactions as AIParseResult[]
        if (txs.length === 0) throw new Error('could_not_parse')
        showToast(`Importando ${txs.length} transações…`)
        const items = txs.map(tx => ({
          kind: tx.kind,
          title: tx.title,
          amount: maskCurrency(Math.round(tx.amount * 100).toString()),
          category_id: resolveCategoryId(tx.category_suggestion, tx.kind),
          subcategory_id: '',
          card_id: '',
          recurring: false,
          realized: false,
          date: tx.date ?? todayIso(),
          due_date: '',
          recurring_until: '',
          installment_count: 1,
          installment_mode: 'per_installment' as const,
        }))
        if (onBatchSubmit) {
          await onBatchSubmit(items)
        } else {
          for (const item of items) await onSubmit(item)
        }
        return
      }

      // Single transaction
      const result = data as AIParseResult
      if ('error' in result) throw new Error('could_not_parse')

      const needsReview = (result.clarification_needed ?? []).length > 0

      if (!needsReview) {
        // Auto-submit when AI is confident
        showToast('Importando transação…')
        await onSubmit({
          kind: result.kind,
          title: result.title,
          amount: maskCurrency(Math.round(result.amount * 100).toString()),
          category_id: resolveCategoryId(result.category_suggestion, result.kind),
          subcategory_id: '',
          card_id: '',
          realized: false,
          recurring: false,
          date: result.date ?? todayIso(),
          due_date: '',
          recurring_until: '',
          installment_count: 1,
          installment_mode: 'per_installment' as const,
        })
        return
      }

      // Pre-fill form for review when AI flags uncertainty
      if (result.kind) {
        setKind(result.kind)
        setExpenseType(result.kind === 'out' ? 'fixed' : null)
      }
      if (result.title) setTitle(result.title)
      if (typeof result.amount === 'number' && result.amount > 0) {
        setAmountDigits(Math.round(result.amount * 100).toString())
      }
      if (result.date) setDate(result.date)
      setCategoryId(resolveCategoryId(result.category_suggestion, result.kind))
      setAiClarificationFields(result.clarification_needed ?? [])
      setAiState('done')
      showToast('Revise os campos destacados e salve')
    } catch {
      setAiState('error')
      showToast('Não consegui identificar a transação. Tente uma foto mais nítida ou descreva o gasto por extenso.')
      setTimeout(() => setAiState('idle'), 2000)
    }
  }

  async function handleSubmit() {
    const errs: { kind?: string; expenseType?: string; title?: string; amount?: string; date?: string; card?: string } = {}
    if (!kind) errs.kind = 'Selecione o tipo de lançamento'
    if (kind === 'out' && !expenseType) errs.expenseType = 'Selecione se é fixo ou no cartão'
    if (!title.trim()) errs.title = 'Descrição é obrigatória'
    else if (title.trim().length > 100) errs.title = 'Máximo 100 caracteres'
    if (!validateDigits(amountDigits)) errs.amount = 'Valor deve ser maior que zero'
    if (!date) errs.date = 'Data de cadastro é obrigatória'
    if (kind === 'out' && expenseType === 'card' && !cardId) errs.card = 'Selecione um cartão'

    if (Object.keys(errs).length > 0) { setErrors(errs); return }

    setSubmitting(true)
    setSubmitError(null)
    try {
      const isInstallment = kind === 'out' && expenseType === 'card' && installmentCount > 1
      const formData = {
        kind: kind!, title, amount: maskCurrency(amountDigits), category_id: categoryId,
        subcategory_id: subcategoryId,
        card_id: kind === 'out' && expenseType === 'card' ? cardId : '',
        recurring: isInstallment ? false : recurring,
        realized,
        date, due_date: isInstallment ? '' : dueDate,
        recurring_until: isInstallment ? '' : recurringUntil,
        installment_count: isInstallment ? installmentCount : 1,
        installment_mode: isInstallment ? installmentMode : 'per_installment',
      }
      if (editing && onUpdate) {
        await onUpdate(editing.id, formData)
      } else {
        await onSubmit(formData)
      }
    } catch (err) {
      setSubmitError((err as Error).message ?? 'Erro ao salvar lançamento')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bc-sheet-overlay" onClick={onClose}>
      <div className="bc-sheet" onClick={e => e.stopPropagation()}>
        <div className="bc-sheet__header">
          <h2 className="bc-sheet__title">{editing ? 'Editar lançamento' : 'Novo lançamento'}</h2>
          <div className="bc-sheet__header-actions">
            <AIImportButton state={aiState} planTier={planTier} onClick={() => setAiModalOpen(true)} />
            <button className="bc-iconbtn" onClick={onClose} aria-label="Fechar">
              <X size={18} />
            </button>
          </div>
        </div>

        {toast && (
          <div className={'bc-toast' + (aiState === 'error' ? ' bc-toast--error' : ' bc-toast--success')}>
            {toast}
          </div>
        )}

        <AIImportModal
          open={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          onFileSelected={handleAiFile}
        />

        <div className="bc-sheet__body">
          <div className="bc-segment" data-sheet-tour="kind">
            <button
              className={'bc-segment__opt ' + (kind === 'in' ? 'is-active is-in' : '')}
              onClick={() => switchKind('in')}
            >
              <ArrowDownRight size={16} /><span>Entrada</span>
            </button>
            <button
              className={'bc-segment__opt ' + (kind === 'out' ? 'is-active is-out' : '')}
              onClick={() => switchKind('out')}
            >
              <ArrowUpRight size={16} /><span>Saída</span>
            </button>
          </div>
          {errors.kind && <span className="bc-field__error">{errors.kind}</span>}

          {kind === 'out' && (
            <>
              <div className="bc-segment">
                <button
                  className={'bc-segment__opt ' + (expenseType === 'fixed' ? 'is-active is-out' : '')}
                  onClick={() => { setExpenseType('fixed'); setCardId(''); setInstallmentCount(1); setErrors(p => ({ ...p, expenseType: undefined, card: undefined })) }}
                >
                  <Receipt size={16} /><span>Fixo / Outros</span>
                </button>
                <button
                  className={'bc-segment__opt ' + (expenseType === 'card' ? 'is-active is-out' : '')}
                  onClick={() => { setExpenseType('card'); setDueDate(''); setErrors(p => ({ ...p, expenseType: undefined })) }}
                >
                  <CreditCard size={16} /><span>No cartão</span>
                </button>
              </div>
              {errors.expenseType && <span className="bc-field__error">{errors.expenseType}</span>}
            </>
          )}

          {canShowMainFields && (
            <>
          <div className="bc-tips" style={{ marginBottom: 16, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
            <button
              onClick={() => setShowTips(!showTips)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'space-between', background: 'none', border: 'none', color: 'var(--ink-400)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <HelpCircle size={16} />
                Dicas de preenchimento
              </div>
              {showTips ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showTips && (
              <ul style={{ margin: '12px 0 0', paddingLeft: 20, color: 'var(--ink-500)', fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <li><strong>Categorias:</strong> Use categorias amplas e deixe detalhes para a descrição.</li>
                <li><strong>Parcelamentos:</strong> Se for no cartão, digite o total e selecione quantas vezes no campo correspondente.</li>
                <li><strong>Efetivado:</strong> Deixe desmarcado se for uma conta pro futuro, e marque apenas quando pagar/receber.</li>
              </ul>
            )}
          </div>

          {kind === 'out' && expenseType === 'card' && (
            <div className="bc-field">
              <label>Cartão</label>
              <div className="bc-cat-grid">
                {activeCards.map(c => (
                  <button
                    key={c.id}
                    className={'bc-cat-pill ' + (cardId === c.id ? 'is-active' : '')}
                    onClick={() => { setCardId(c.id); setErrors(p => ({ ...p, card: undefined })); setShowCardForm(false) }}
                  >
                    {c.name}{c.last4 ? ` ••${c.last4}` : ''}{c.is_archived ? ' (Arquivado)' : ''}
                  </button>
                ))}
                {addCard && !showCardForm && (
                  <button
                    className="bc-cat-pill bc-cat-pill--new"
                    onClick={() => { setShowCardForm(true); setCardForm(BLANK_CARD); setCardFormError(null) }}
                  >
                    <Plus size={12} /> Novo cartão
                  </button>
                )}
              </div>
              {errors.card && <span className="bc-field__error">{errors.card}</span>}

              {showCardForm && addCard && (
                <div className="bc-inline-card-form">
                  <div className="bc-inline-card-form__grid">
                    <div className="bc-field">
                      <label className="bc-label">Banco / nome</label>
                      <input
                        className="bc-input"
                        value={cardForm.name}
                        onChange={e => setCardForm(p => ({ ...p, name: e.target.value }))}
                        maxLength={40}
                        placeholder="Nubank"
                        autoFocus
                      />
                    </div>
                    <div className="bc-field">
                      <label className="bc-label">Últimos 4 dígitos <span className="bc-label__opt">(opcional)</span></label>
                      <input
                        className="bc-input"
                        value={cardForm.last4}
                        onChange={e => setCardForm(p => ({ ...p, last4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                        maxLength={4}
                        placeholder="1234"
                        inputMode="numeric"
                      />
                    </div>
                    <div className="bc-field">
                      <label className="bc-label">Limite</label>
                      <div className="bc-currency">
                        <span className="bc-currency__prefix">R$</span>
                        <input
                          value={maskCurrency(cardForm.limitDigits)}
                          onChange={e => setCardForm(p => ({ ...p, limitDigits: e.target.value.replace(/\D/g, '').slice(0, 12) }))}
                          placeholder="0,00"
                          inputMode="numeric"
                        />
                      </div>
                    </div>
                    <div className="bc-field">
                      <label className="bc-label">Dia do fechamento</label>
                      <input
                        className="bc-input"
                        value={cardForm.close}
                        onChange={e => setCardForm(p => ({ ...p, close: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                        onBlur={e => {
                          const v = e.target.value.trim()
                          if (v.length === 1) setCardForm(p => ({ ...p, close: v.padStart(2, '0') }))
                        }}
                        maxLength={2}
                        placeholder="5"
                        inputMode="numeric"
                      />
                    </div>
                    <div className="bc-field">
                      <label className="bc-label">Dia do vencimento</label>
                      <input
                        className="bc-input"
                        value={cardForm.due}
                        onChange={e => setCardForm(p => ({ ...p, due: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                        onBlur={e => {
                          const v = e.target.value.trim()
                          if (v.length === 1) setCardForm(p => ({ ...p, due: v.padStart(2, '0') }))
                        }}
                        maxLength={2}
                        placeholder="15"
                        inputMode="numeric"
                      />
                    </div>
                    <div className="bc-field" style={{ gridColumn: '1 / -1' }}>
                      <label className="bc-label">Cor</label>
                      <div className="bc-cc-grid">
                        {PRESET_COLORS.map(color => (
                          <button key={color} type="button"
                            className={'bc-cc-mini' + (cardForm.tone === color ? ' bc-cc-mini--sel' : '')}
                            style={{ background: color }}
                            onClick={() => setCardForm(p => ({ ...p, tone: color }))}
                            aria-label={color}
                          />
                        ))}
                        <label
                          className={'bc-cc-mini bc-cc-mini--custom' + (!PRESET_COLORS.includes(cardForm.tone) ? ' bc-cc-mini--sel' : '')}
                          style={!PRESET_COLORS.includes(cardForm.tone) ? { background: cardForm.tone } : undefined}
                          title="Cor personalizada"
                        >
                          {PRESET_COLORS.includes(cardForm.tone) && <span className="bc-cc-mini__plus">+</span>}
                          <input type="color" className="bc-cc-mini__input"
                            value={PRESET_COLORS.includes(cardForm.tone) ? '#000000' : cardForm.tone}
                            onChange={e => setCardForm(p => ({ ...p, tone: e.target.value }))}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  {cardFormError && <p className="bc-field__error" style={{ marginTop: 8 }}>{cardFormError}</p>}
                  <div className="bc-inline-card-form__footer">
                    <button
                      className="bc-btn bc-btn--ghost"
                      onClick={() => { setShowCardForm(false); setCardFormError(null) }}
                      disabled={cardFormSaving}
                    >
                      <X size={14} /> Cancelar
                    </button>
                    <button
                      className="bc-btn bc-btn--primary"
                      disabled={cardFormSaving}
                      onClick={async () => {
                        const err = validateCardForm(cardForm)
                        if (err) { setCardFormError(err); return }
                        setCardFormSaving(true)
                        setCardFormError(null)
                        try {
                          const created = await addCard({
                            name: cardForm.name.trim(),
                            last4: cardForm.last4.trim() || null,
                            tone: cardForm.tone,
                            bill: 0,
                            limit: digitsToAmount(cardForm.limitDigits),
                            due: parseInt(cardForm.due.trim(), 10),
                            close: parseInt(cardForm.close.trim(), 10),
                            is_archived: false,
                          })
                          setCardId(created.id)
                          setErrors(p => ({ ...p, card: undefined }))
                          setShowCardForm(false)
                        } catch (e) {
                          setCardFormError((e as Error).message)
                        } finally {
                          setCardFormSaving(false)
                        }
                      }}
                    >
                      <Check size={14} /> {cardFormSaving ? '…' : 'Adicionar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bc-field" data-sheet-tour="title">
            <label>Descrição</label>
            <input
              className={'bc-input' + (errors.title ? ' is-error' : '')}
              style={aiClarificationFields.includes('title') ? { borderColor: 'var(--amber-500)' } : undefined}
              placeholder="Ex: Mercado mensal"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTitle(e.target.value)
                if (errors.title) setErrors(prev => ({ ...prev, title: undefined }))
              }}
            />
            {errors.title && <span className="bc-field__error">{errors.title}</span>}
          </div>

          {kind === 'out' && expenseType === 'card' && (
            <div className="bc-segment bc-segment--sm">
              <button
                type="button"
                className={'bc-segment__opt' + (installmentMode === 'per_installment' ? ' is-active is-out' : '')}
                onClick={() => setInstallmentMode('per_installment')}
              >
                Valor da parcela
              </button>
              <button
                type="button"
                className={'bc-segment__opt' + (installmentMode === 'total' ? ' is-active is-out' : '')}
                onClick={() => setInstallmentMode('total')}
              >
                Valor total
              </button>
            </div>
          )}

          <div className={kind === 'out' && expenseType === 'card' ? 'bc-field-row bc-field-row--2-1' : ''}>
            <div className="bc-field" data-sheet-tour="amount">
              <label>
                {kind === 'out' && expenseType === 'card' && installmentCount > 1 && installmentMode === 'total'
                  ? 'Valor total da compra'
                  : 'Valor'}
              </label>
              <div
                className={'bc-currency' + (errors.amount ? ' is-error' : '')}
                style={aiClarificationFields.includes('amount') ? { borderColor: 'var(--amber-500)' } : undefined}
              >
                <span className="bc-currency__prefix">R$</span>
                <input
                  value={maskCurrency(amountDigits)}
                  placeholder="0,00"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 12)
                    setAmountDigits(digits)
                    if (errors.amount) setErrors(prev => ({ ...prev, amount: undefined }))
                  }}
                  inputMode="numeric"
                />
              </div>
              {errors.amount && <span className="bc-field__error">{errors.amount}</span>}
            </div>

            {kind === 'out' && expenseType === 'card' && (
              <div className="bc-field">
                <label>Parcelas</label>
                <input
                  type="number"
                  className="bc-input"
                  min={1}
                  max={48}
                  value={installmentCount}
                  onChange={e => {
                    const next = Math.max(1, Math.min(48, parseInt(e.target.value, 10) || 1))
                    setInstallmentCount(next)
                    if (next > 1) setRecurring(false)
                  }}
                  inputMode="numeric"
                />
              </div>
            )}
            {kind === 'out' && expenseType === 'card' && !editing && installmentCount > 1 && (
              <span className="bc-field__hint" style={{ gridColumn: '1 / -1' }}>
                {installmentMode === 'per_installment'
                  ? (title.trim()
                      ? `"${title.trim()} (1/${installmentCount})" até "${title.trim()} (${installmentCount}/${installmentCount})" · Total: ${
                          amountDigits ? `R$ ${maskCurrency(String(Number(amountDigits) * installmentCount))}` : '—'
                        }`
                      : `Serão criadas ${installmentCount} parcelas`)
                  : (amountDigits
                      ? `${installmentCount} parcelas de R$ ${maskCurrency(String(Math.floor(Number(amountDigits) / installmentCount)))}`
                      : `${installmentCount} parcelas de R$ —`)
                }
              </span>
            )}
          </div>

          <div className="bc-field" data-sheet-tour="category">
            <label>Categoria</label>
            <div className="bc-cat-grid">
              {cats.map(c => (
                <button
                  key={c.id}
                  className={'bc-cat-pill ' + (categoryId === c.id ? 'is-active' : '')}
                  onClick={() => { selectCategory(c.id); setShowCategoryForm(false) }}
                >
                  {c.name}
                </button>
              ))}
              {addCategory && kind && !showCategoryForm && (
                <button
                  className="bc-cat-pill bc-cat-pill--new"
                  onClick={() => { setShowCategoryForm(true); setCategoryFormName(''); setCategoryFormLimitDigits(''); setCategoryFormError(null) }}
                >
                  <Plus size={12} /> Nova categoria
                </button>
              )}
            </div>
            {cats.length === 0 && !showCategoryForm && (
              <p style={{ fontSize: '0.8125rem', color: 'var(--ink-500)', marginTop: 4 }}>
                Nenhuma categoria encontrada.
              </p>
            )}

            {showCategoryForm && addCategory && kind && (
              <div className="bc-inline-card-form" style={{ marginTop: 8 }}>
                <div className="bc-field">
                  <label className="bc-label">Nome</label>
                  <input
                    className="bc-input"
                    value={categoryFormName}
                    onChange={e => { setCategoryFormName(e.target.value); setCategoryFormError(null) }}
                    maxLength={50}
                    placeholder="Ex: Alimentação"
                    autoFocus
                  />
                </div>
                {kind === 'out' && (
                  <div className="bc-field" style={{ marginTop: 8 }}>
                    <label className="bc-label">Limite de gasto <span style={{ fontWeight: 400, color: 'var(--ink-500)' }}>opcional</span></label>
                    <input
                      className="bc-input"
                      inputMode="numeric"
                      value={maskCurrency(categoryFormLimitDigits)}
                      onChange={e => setCategoryFormLimitDigits(e.target.value.replace(/\D/g, '').slice(0, 12))}
                      placeholder="R$ 0,00"
                    />
                  </div>
                )}
                {categoryFormError && <p className="bc-field__error" style={{ marginTop: 4 }}>{categoryFormError}</p>}
                <div className="bc-inline-card-form__footer">
                  <button
                    className="bc-btn bc-btn--ghost"
                    onClick={() => { setShowCategoryForm(false); setCategoryFormError(null) }}
                    disabled={categoryFormSaving}
                  >
                    <X size={14} /> Cancelar
                  </button>
                  <button
                    className="bc-btn bc-btn--primary"
                    disabled={categoryFormSaving}
                    onClick={async () => {
                      const trimmed = categoryFormName.trim()
                      if (!trimmed) { setCategoryFormError('Nome obrigatório'); return }
                      if (trimmed.length > 50) { setCategoryFormError('Máximo 50 caracteres'); return }
                      const existing = cats.find(c => c.name.toLowerCase() === trimmed.toLowerCase())
                      if (existing) {
                        selectCategory(existing.id)
                        setShowCategoryForm(false)
                        return
                      }
                      const limit = kind === 'out' && categoryFormLimitDigits ? digitsToAmount(categoryFormLimitDigits) : null
                      setCategoryFormSaving(true)
                      setCategoryFormError(null)
                      try {
                        const created = await addCategory(trimmed, kind, limit)
                        selectCategory(created.id)
                        setShowCategoryForm(false)
                      } catch (e) {
                        setCategoryFormError((e as Error).message)
                      } finally {
                        setCategoryFormSaving(false)
                      }
                    }}
                  >
                    <Check size={14} /> {categoryFormSaving ? '…' : 'Confirmar'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {subCats.length > 0 && (
            <div className="bc-field">
              <label>Subcategoria <span style={{ fontWeight: 400, color: 'var(--ink-4)' }}>opcional</span></label>
              <div className="bc-cat-grid">
                {subCats.map(s => (
                  <button
                    key={s.id}
                    className={'bc-cat-pill ' + (subcategoryId === s.id ? 'is-active' : '')}
                    onClick={() => setSubcategoryId(prev => prev === s.id ? '' : s.id)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bc-field-row">
            <div className="bc-field">
              <label>Data de cadastro</label>
              <input
                type="date"
                className={'bc-input' + (errors.date ? ' is-error' : '')}
                style={aiClarificationFields.includes('date') ? { borderColor: 'var(--amber-500)' } : undefined}
                value={date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDate(e.target.value)
                  if (errors.date) setErrors(prev => ({ ...prev, date: undefined }))
                }}
              />
              {errors.date && <span className="bc-field__error">{errors.date}</span>}
            </div>

            {expenseType !== 'card' && (
              <div className="bc-field">
                <label>Data de vencimento</label>
                <input
                  type="date"
                  className="bc-input"
                  value={dueDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDueDate(e.target.value)}
                />
              </div>
            )}
          </div>

          <label className="bc-check">
            <input
              type="checkbox"
              checked={realized}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRealized(e.target.checked)}
            />
            <span>
              {kind === 'in' ? 'Recebi' : 'Paguei'}
            </span>
          </label>

          {canRepeat && (
            <label className="bc-check" data-sheet-tour="recurring">
              <input
                type="checkbox"
                checked={recurring}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setRecurring(e.target.checked)
                  if (!e.target.checked) setRecurringUntil('')
                }}
              />
              <span>Repetir todo mês</span>
            </label>
          )}

          {recurring && canRepeat && (
            <div className="bc-field">
              <label>Repetir até</label>
              <input
                type="date"
                className="bc-input"
                value={recurringUntil}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRecurringUntil(e.target.value)}
              />
            </div>
          )}
            </>
          )}
        </div>

        <div className="bc-sheet__footer">
          {submitError && <span className="bc-field__error bc-sheet__submit-error">{submitError}</span>}
          <button className="bc-btn bc-btn--ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          {canShowMainFields && (
            <button
              className={'bc-btn ' + (kind === 'in' ? 'bc-btn--income' : 'bc-btn--expense')}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Salvando…' : editing ? 'Salvar alterações' : 'Salvar lançamento'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
